import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCar,
  FaCheckCircle,
  FaTools,
  FaBan,
  FaSearch,
  FaPlus,
  FaFilter,
  FaEdit,
  FaUser,
  FaCog,
  FaSuitcase,
  FaGasPump,
  FaChevronDown,
  FaChevronUp,
  FaThLarge,
  FaList,
  FaSort,
  FaTags,
  FaRegCalendarAlt,
  FaTrash,
  FaClipboardList,
  FaWrench,
  FaDollarSign,
  FaCalendarAlt,
  FaPercent,
  FaCopy,
  FaSave,
  FaChartLine,
  FaArrowLeft,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { cars as carsAPI } from '../../../services/api';

function Cars({
  carsData,
  searchQuery,
  setSearchQuery,
  isCreatingCar,
  setCreateCarModalOpen,
  selectedCategory,
  setSelectedCategory,
  selectedTransmission,
  setSelectedTransmission,
  categories,
  paginatedCars,
  handleEditCar,
  getImagePath,
  handleCarDelete,
  handleSeasonSelect,
  updatingCarId,
  deletingCarId,
  settingSeasonalPrice,
  editingSeasonalPrice,
  setEditingSeasonalPrice,
  seasonalPricing,
  createCarModalOpen,
  handleCreateCar,
  selectedCar,
  carDetailsModalOpen,
  setCarDetailsModalOpen,
  setSelectedCar,
  handleCarUpdate,
  totalPages,
  setCarPage,
  carPage,
  LoadingSpinner,
  CreateCarModal,
  CarDetailsModal,
}) {
  const navigate = useNavigate();
  
  // Tab management state
  const [activeTab, setActiveTab] = useState("cars"); // "cars" or "pricing"
  
  // New state variables for enhanced UI
  const [viewMode, setViewMode] = useState("grid"); // grid or list view
  const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(false);
  const [activeStatusFilter, setActiveStatusFilter] = useState("all");
  const [sortOption, setSortOption] = useState("newest");
  
  // Advanced filter state
  const [selectedFuelType, setSelectedFuelType] = useState("all");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [minSeats, setMinSeats] = useState("all");
  const [filteredCars, setFilteredCars] = useState([]);
  const [filteredPages, setFilteredPages] = useState(1);
  const [currentFilteredPage, setCurrentFilteredPage] = useState(1);
  const ITEMS_PER_PAGE = 9; // Number of cars to show per page
  
  // License plate search state
  const [licensePlateResults, setLicensePlateResults] = useState([]);
  const [isLicensePlateSearch, setIsLicensePlateSearch] = useState(false);
  const [searchingLicensePlate, setSearchingLicensePlate] = useState(false);
  // Pricing management state
  const [selectedCars, setSelectedCars] = useState([]);
  const [pricingMode, setPricingMode] = useState("single"); // "single" or "bulk"
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: ""
  });
  const [pricingData, setPricingData] = useState({});
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [savingPricing, setSavingPricing] = useState(false);
  const [bulkPriceValue, setBulkPriceValue] = useState("");
  const [bulkPriceType, setBulkPriceType] = useState("fixed"); // "fixed" or "percentage"
  const [showPricingCalendar, setShowPricingCalendar] = useState(false);
  const [selectedPricingCar, setSelectedPricingCar] = useState(null);
    // Seasonal pricing configuration
  const [seasonalPrices, setSeasonalPrices] = useState({
    low: "25",
    medium: "35", 
    high: "45"
  });
  
  // Seasonal pricing configuration  
  // Function to detect if search query looks like a license plate
  const isLicensePlatePattern = (query) => {
    // Greek license plate patterns: ΥΤΗ-3443, YTH-3443, ΥΤΗ3443, YTH3443, etc.
    const patterns = [
      /^[Α-ΩA-Z]{2,3}[-\s]?\d{3,4}$/i, // Standard format: ABC-1234 or ABC1234
      /^\d{3,4}[-\s]?[Α-ΩA-Z]{2,3}$/i, // Reverse format: 1234-ABC or 1234ABC
      /^[Α-ΩA-Z]{3}[-\s]?\d{4}$/i,     // Three letters, four numbers
    ];
    
    return patterns.some(pattern => pattern.test(query.trim()));
  };
  
  // Function to search by license plate using API service
  const searchByLicensePlate = async (plate) => {
    if (!plate || plate.trim().length < 3) {
      setLicensePlateResults([]);
      setIsLicensePlateSearch(false);
      return;
    }
    
    setSearchingLicensePlate(true);
    try {
      const response = await carsAPI.searchByLicensePlate(plate);
      
      const results = response.data;
      setLicensePlateResults(results);
      setIsLicensePlateSearch(true);
      console.log('License plate search results:', results);
    } catch (error) {
      console.error('Error searching by license plate:', error);
      setLicensePlateResults([]);
      setIsLicensePlateSearch(false);
    } finally {
      setSearchingLicensePlate(false);
    }
  };
  
  // Effect to handle search query changes
  useEffect(() => {
    const trimmedQuery = searchQuery.trim();
    
    if (trimmedQuery === '') {
      setIsLicensePlateSearch(false);
      setLicensePlateResults([]);
      return;
    }
    
    if (isLicensePlatePattern(trimmedQuery)) {
      // Debounce the license plate search
      const timeoutId = setTimeout(() => {
        searchByLicensePlate(trimmedQuery);
      }, 500);
      
      return () => clearTimeout(timeoutId);
    } else {
      setIsLicensePlateSearch(false);
      setLicensePlateResults([]);
    }
  }, [searchQuery]);

  // Apply all filters to the ENTIRE car dataset, not just the paginated results
  const applyFilters = (allCars) => {
    // If we're in license plate search mode, use those results instead
    if (isLicensePlateSearch && licensePlateResults.length > 0) {
      return licensePlateResults;
    }
    
    let result = [...allCars];
    
    // Apply category filter first
    if (selectedCategory !== "all") {
      result = result.filter(car => car.category === selectedCategory);
    }
    
    // Apply transmission filter
    if (selectedTransmission !== "all") {
      result = result.filter(car => car.transmission === selectedTransmission);
    }
    
    // Apply status filter
    if (activeStatusFilter !== "all") {
      result = result.filter(car => car.status === activeStatusFilter);
    }
    
    // Apply fuel type filter
    if (selectedFuelType !== "all") {
      result = result.filter(car => 
        car.fuelType && car.fuelType.toLowerCase() === selectedFuelType.toLowerCase()
      );
    }
    
    // Apply price range filters
    if (minPrice !== "" && !isNaN(minPrice)) {
      result = result.filter(car => car.basePrice >= Number(minPrice));
    }
    
    if (maxPrice !== "" && !isNaN(maxPrice)) {
      result = result.filter(car => car.basePrice <= Number(maxPrice));
    }
    
    // Apply minimum seats filter
    if (minSeats !== "all") {
      result = result.filter(car => car.seats >= Number(minSeats));
    }
    
    // Apply search query (only if not license plate search)
    if (searchQuery.trim() !== "" && !isLicensePlateSearch) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(car => 
        (car.make && car.make.toLowerCase().includes(query)) ||
        (car.model && car.model.toLowerCase().includes(query)) ||
        (car.year && car.year.toString().includes(query))
      );
    }

      // Function to sort cars based on selected option
  const sortCars = (cars, option) => {
    const sorted = [...cars];
    
    switch(option) {
      case "newest":
        return sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      case "oldest":
        return sorted.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
      case "price-low":
        return sorted.sort((a, b) => (a.basePrice || 0) - (b.basePrice || 0));
      case "price-high":
        return sorted.sort((a, b) => (b.basePrice || 0) - (a.basePrice || 0));
      case "popular":
        // Assuming there's a popularity/bookings count field, fallback to units if not
        return sorted.sort((a, b) => (b.bookingsCount || b.totalUnits || 0) - (a.bookingsCount || a.totalUnits || 0));
      default:
        return sorted;
    }
  };
    
    // Apply sorting
    result = sortCars(result, sortOption);
    
    return result;
  };
  
  // Use useMemo to calculate filtered cars and pagination only when dependencies change
  const filteredResults = useMemo(() => {
    const allFilteredCars = applyFilters(carsData);
    const totalFilteredPages = Math.ceil(allFilteredCars.length / ITEMS_PER_PAGE);
    
    // Reset to page 1 if our current page exceeds the new total
    if (currentFilteredPage > totalFilteredPages) {
      setCurrentFilteredPage(1);
    }
    
    // Calculate the current page of filtered results
    const startIndex = (currentFilteredPage - 1) * ITEMS_PER_PAGE;
    const paginatedFiltered = allFilteredCars.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    
    return {
      allFiltered: allFilteredCars,
      totalPages: totalFilteredPages,
      currentPageItems: paginatedFiltered
    };
  }, [
    carsData, 
    selectedCategory,
    selectedTransmission,
    activeStatusFilter, 
    selectedFuelType, 
    minPrice, 
    maxPrice, 
    minSeats,
    searchQuery,
    sortOption,
    currentFilteredPage,
    ITEMS_PER_PAGE,
    isLicensePlateSearch,
    licensePlateResults
  ]);
  
  // Update filtered cars state whenever filteredResults changes
  useEffect(() => {
    setFilteredCars(filteredResults.currentPageItems);
    setFilteredPages(filteredResults.totalPages);
  }, [filteredResults]);
  
  // Reset filtered page when filters change
  useEffect(() => {
    setCurrentFilteredPage(1);
  }, [
    selectedCategory,
    selectedTransmission,
    activeStatusFilter, 
    selectedFuelType, 
    minPrice, 
    maxPrice, 
    minSeats,
    searchQuery,
    sortOption
  ]);


    // Reset all filters function
  const resetAllFilters = () => {
    setSearchQuery("");
    setSelectedCategory("all");
    setSelectedTransmission("all");
    setActiveStatusFilter("all");
    setSelectedFuelType("all");
    setMinPrice("");
    setMaxPrice("");
    setMinSeats("all");
    setSortOption("newest");
    setCurrentFilteredPage(1); // Reset to first page of filtered results
    setIsLicensePlateSearch(false);
    setLicensePlateResults([]);
  };
  
  // Determine if we have any cars to display
  const hasNoCars = filteredCars.length === 0;
  const totalFilteredCount = useMemo(() => filteredResults?.allFiltered?.length || 0, [filteredResults]);

  // Function to render the appropriate category icon
  const getCategoryIcon = (category) => {
    switch (category?.toLowerCase()) {
      case "compact":
      case "economy":
        return <FaCar className="mr-2" />;
      case "suv":
        return <FaSuitcase className="mr-2" />;
      case "luxury":
        return <FaTags className="mr-2" />;
      default:
        return <FaCar className="mr-2" />;
    }
  };
  // Function to handle status filter changes
  const handleStatusFilterChange = (status) => {
    setActiveStatusFilter(status);
    setCurrentFilteredPage(1); // Reset to first page when filter changes
  };
  
  // Calculate vehicle counts per category and total vehicles
  const vehicleStats = useMemo(() => {
    let totalVehicles = 0;
    let availableVehicles = 0;
    let maintenanceVehicles = 0;
    let unavailableVehicles = 0;
    
    const categoryCounts = {};
    
    // Initialize category counts
    categories.forEach(category => {
      categoryCounts[category] = 0;
    });
    
    // Calculate vehicle counts from all car instances
    carsData.forEach(car => {
      if (car.vehicleInstances && car.vehicleInstances.length > 0) {
        car.vehicleInstances.forEach(vehicle => {
          totalVehicles++;
          
          // Count by category
          if (car.category) {
            categoryCounts[car.category] = (categoryCounts[car.category] || 0) + 1;
          }
          
          // Count by status
          switch (vehicle.status) {
            case 'available':
              availableVehicles++;
              break;
            case 'maintenance':
              maintenanceVehicles++;
              break;
            case 'unavailable':
              unavailableVehicles++;
              break;
            default:
              break;
          }
        });
      }
    });
    
    return {
      totalVehicles,
      availableVehicles,
      maintenanceVehicles,
      unavailableVehicles,
      categoryCounts
    };
  }, [carsData, categories]);

  // Enhanced handleCarUpdate to handle image updates
  const handleCarUpdateWithImages = (carId, carData, isImageUpdate = false) => {
    handleCarUpdate(carId, carData);
  };

  // Pricing Management Functions
  const loadCarPricing = async (carId, startDate, endDate) => {
    if (!startDate || !endDate) return;
    
    setLoadingPricing(true);
    try {
      const response = await carsAPI.getPricing(carId, { startDate, endDate });
      setPricingData(prev => ({
        ...prev,
        [carId]: response.data
      }));
    } catch (error) {
      console.error('Error loading car pricing:', error);
    } finally {
      setLoadingPricing(false);
    }
  };

  const updateCarPricing = async (carId, dates) => {
    setSavingPricing(true);
    try {
      for (const dateData of dates) {
        await carsAPI.updateAvailability(carId, dateData);
      }
      
      // Reload pricing data
      if (dateRange.startDate && dateRange.endDate) {
        await loadCarPricing(carId, dateRange.startDate, dateRange.endDate);
      }
      
      return true;
    } catch (error) {
      console.error('Error updating car pricing:', error);
      return false;
    } finally {
      setSavingPricing(false);
    }
  };

  const generateDateRange = (start, end) => {
    const dates = [];
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d).toISOString().split('T')[0]);
    }
    
    return dates;
  };
  const handleBulkPriceUpdate = async () => {
    if (!dateRange.startDate || !dateRange.endDate || selectedCars.length === 0 || !bulkPriceValue) {
      return;
    }

    setSavingPricing(true);
    try {
      const dates = generateDateRange(dateRange.startDate, dateRange.endDate);
      
      for (const carId of selectedCars) {
        const car = carsData.find(c => c._id === carId);
        if (!car) continue;

        const pricingUpdates = dates.map(date => {
          let price;
          if (bulkPriceType === "fixed") {
            price = parseFloat(bulkPriceValue);
          } else {
            // Percentage adjustment
            const basePrice = car.basePrice;
            const adjustment = parseFloat(bulkPriceValue) / 100;
            price = basePrice + (basePrice * adjustment);
          }

          return {
            date: date,
            isAvailable: true,
            dynamicPrice: Math.round(price * 100) / 100
          };
        });

        await updateCarPricing(carId, pricingUpdates);
      }

      // Clear selections
      setSelectedCars([]);
      setBulkPriceValue("");
      
    } catch (error) {
      console.error('Error updating bulk pricing:', error);
    } finally {
      setSavingPricing(false);
    }
  };



  const copyPricingFromCar = async (sourceCar, targetCarIds) => {
    if (!dateRange.startDate || !dateRange.endDate || !sourceCar.availability) {
      return;
    }

    setSavingPricing(true);
    try {
      const dates = generateDateRange(dateRange.startDate, dateRange.endDate);
      
      for (const targetCarId of targetCarIds) {
        const pricingUpdates = dates.map(date => {
          const existingPricing = sourceCar.availability.find(
            a => new Date(a.date).toISOString().split('T')[0] === date
          );
          
          return {
            date: date,
            isAvailable: true,
            dynamicPrice: existingPricing?.dynamicPrice || sourceCar.basePrice
          };
        });

        await updateCarPricing(targetCarId, pricingUpdates);
      }
    } catch (error) {
      console.error('Error copying pricing:', error);
    } finally {
      setSavingPricing(false);
    }
  };


  const calculatePricingStats = (car) => {
    if (!car.availability || car.availability.length === 0) {
      return {
        averagePrice: car.basePrice,
        minPrice: car.basePrice,
        maxPrice: car.basePrice,
        totalDays: 0,
        seasonalBreakdown: {
          low: { count: 0, avgPrice: car.basePrice },
          medium: { count: 0, avgPrice: car.basePrice },
          high: { count: 0, avgPrice: car.basePrice },
          custom: { count: 0, avgPrice: car.basePrice }
        }
      };
    }

    // Get prices from availability array (each entry has dynamicPrice)
    const prices = car.availability.map(a => a.dynamicPrice || car.basePrice);
    
    // Calculate seasonal breakdown
    const seasonalBreakdown = {
      low: { count: 0, totalPrice: 0, avgPrice: 0 },
      medium: { count: 0, totalPrice: 0, avgPrice: 0 },
      high: { count: 0, totalPrice: 0, avgPrice: 0 },
      custom: { count: 0, totalPrice: 0, avgPrice: 0 }
    };

    car.availability.forEach(pricing => {
      const date = new Date(pricing.date);
      const season = getSeasonForDate(date);
      const price = pricing.dynamicPrice || car.basePrice;
      
      if (seasonalBreakdown[season]) {
        seasonalBreakdown[season].count++;
        seasonalBreakdown[season].totalPrice += price;
      }
    });

    // Calculate averages
    Object.keys(seasonalBreakdown).forEach(season => {
      const data = seasonalBreakdown[season];
      data.avgPrice = data.count > 0 ? Math.round((data.totalPrice / data.count) * 100) / 100 : car.basePrice;
    });

    return {
      averagePrice: Math.round((prices.reduce((a, b) => a + b, 0) / prices.length) * 100) / 100,
      minPrice: Math.min(...prices),
      maxPrice: Math.max(...prices),
      totalDays: car.availability.length,
      seasonalBreakdown
    };
  };



  // Helper function to determine season for a date
  const getSeasonForDate = (date) => {
    const month = date.getMonth() + 1; // 1-based month
    const day = date.getDate();
    
    // Low Season: Apr 1 - May 15, Sep 21 - Oct 30
    if ((month === 4) || 
        (month === 5 && day <= 15) ||
        (month === 9 && day >= 21) ||
        (month === 10 && day <= 30)) {
      return 'low';
    }
    
    // High Season: Jun 16 - Sep 5
    if ((month === 6 && day >= 16) || 
        (month === 7) ||
        (month === 8) ||
        (month === 9 && day <= 5)) {
      return 'high';
    }
    
    // Medium Season: May 16 - Jun 15, Sep 6 - Sep 20
    if ((month === 5 && day >= 16) ||
        (month === 6 && day <= 15) ||
        (month === 9 && day >= 6 && day <= 20)) {
      return 'medium';
    }
    
    // Default to custom for dates outside defined seasons
    return 'custom';  };

  return (
    <>
      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-md mb-6">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab("cars")}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors duration-200 ${
              activeTab === "cars"
                ? "border-blue-500 text-blue-600 bg-blue-50"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <div className="flex items-center">
              <FaCar className="mr-2" />
              Car Management
            </div>
          </button>
          <button
            onClick={() => setActiveTab("pricing")}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors duration-200 ${
              activeTab === "pricing"
                ? "border-blue-500 text-blue-600 bg-blue-50"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <div className="flex items-center">
              <FaDollarSign className="mr-2" />
              Pricing Management
            </div>
          </button>
        </div>
      </div>

      {/* Cars Tab Content */}
      {activeTab === "cars" && (
        <>
          {/* Cars Statistics Dashboard - Enhanced with animated hover and click effects */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleStatusFilterChange("all")}
          className={`bg-gradient-to-br from-blue-500 to-blue-700 p-6 rounded-xl shadow-lg cursor-pointer transition-all ${activeStatusFilter === "all" ? "ring-4 ring-blue-300" : ""}`}
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-full bg-blue-400 bg-opacity-40">
              <FaCar className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-white text-sm opacity-80">Total Cars</p>
              <p className="text-2xl font-bold text-white">{carsData.length}</p>
              <p className="text-white text-xs opacity-70">{vehicleStats.totalVehicles} vehicles</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleStatusFilterChange("available")}
          className={`bg-gradient-to-br from-green-500 to-green-700 p-6 rounded-xl shadow-lg cursor-pointer transition-all ${activeStatusFilter === "available" ? "ring-4 ring-green-300" : ""}`}
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-full bg-green-400 bg-opacity-40">
              <FaCheckCircle className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-white text-sm opacity-80">Available</p>
              <p className="text-2xl font-bold text-white">
                {carsData.filter((car) => car.status === "available").length}
              </p>
              <p className="text-white text-xs opacity-70">{vehicleStats.availableVehicles} vehicles</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleStatusFilterChange("maintenance")}
          className={`bg-gradient-to-br from-yellow-500 to-yellow-700 p-6 rounded-xl shadow-lg cursor-pointer transition-all ${activeStatusFilter === "maintenance" ? "ring-4 ring-yellow-300" : ""}`}
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-full bg-yellow-400 bg-opacity-40">
              <FaTools className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-white text-sm opacity-80">In Maintenance</p>
              <p className="text-2xl font-bold text-white">
                {carsData.filter((car) => car.status === "maintenance").length}
              </p>
              <p className="text-white text-xs opacity-70">{vehicleStats.maintenanceVehicles} vehicles</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleStatusFilterChange("unavailable")}
          className={`bg-gradient-to-br from-red-500 to-red-700 p-6 rounded-xl shadow-lg cursor-pointer transition-all ${activeStatusFilter === "unavailable" ? "ring-4 ring-red-300" : ""}`}
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-full bg-red-400 bg-opacity-40">
              <FaBan className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-white text-sm opacity-80">Unavailable</p>
              <p className="text-2xl font-bold text-white">
                {carsData.filter((car) => car.status === "unavailable").length}
              </p>
              <p className="text-white text-xs opacity-70">{vehicleStats.unavailableVehicles} vehicles</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Enhanced Search and Action Bar */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center justify-between mb-4">
          <div className="flex-1 relative min-w-[250px]">
            <input
              type="text"
              placeholder="Search cars by make, model, year, or license plate (e.g. ΥΤΗ-3443)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 pl-12 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300"
            />
            <FaSearch className="absolute left-4 top-3.5 text-gray-400" />
            {searchingLicensePlate && (
              <div className="absolute right-4 top-3.5">
                <LoadingSpinner size="sm" />
              </div>
            )}
            {isLicensePlateSearch && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-blue-50 border border-blue-200 rounded-lg p-2 text-xs text-blue-700">
                License plate search active - Found {licensePlateResults.length} car(s)
              </div>
            )}
          </div>
          
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode("grid")}
              className={`px-3 py-2 rounded-md flex items-center text-sm ${
                viewMode === "grid"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:bg-gray-200"
              }`}
            >
              <FaThLarge className="mr-1" />
              <span className="hidden sm:inline">Grid</span>
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`px-3 py-2 rounded-md flex items-center text-sm ${
                viewMode === "list"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:bg-gray-200"
              }`}
            >
              <FaList className="mr-1" />
              <span className="hidden sm:inline">List</span>
            </button>
          </div>
          
          {/* Sort dropdown */}
          <div className="relative">
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className="appearance-none bg-gray-100 border border-gray-200 text-gray-700 py-2 px-4 pr-8 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="popular">Most Popular</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <FaSort className="h-4 w-4" />
            </div>
          </div>
          
          {/* Vehicle Service Report Button */}
          <motion.button
            onClick={() => navigate('/vehicle-service-report')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-lg hover:from-purple-700 hover:to-purple-900 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-300 shadow-md"
          >
            <div className="flex items-center">
              <FaClipboardList className="mr-2" />
              <span className="hidden sm:inline">Service Report</span>
              <span className="sm:hidden">Report</span>
            </div>
          </motion.button>
          
          <motion.button
            onClick={() => setCreateCarModalOpen(true)}
            disabled={isCreatingCar}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg hover:from-blue-700 hover:to-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-md"
          >
            {isCreatingCar ? (
              <div className="flex items-center">
                <LoadingSpinner size="sm" />
                <span className="ml-2">Creating...</span>
              </div>
            ) : (
              <div className="flex items-center">
                <FaPlus className="mr-2" />
                <span>Add New Car</span>
              </div>
            )}
          </motion.button>
        </div>
        
        {/* Advanced Filter Toggle Button */}
        <button
          onClick={() => setIsAdvancedFilterOpen(!isAdvancedFilterOpen)}
          className="flex items-center text-gray-600 hover:text-blue-600 text-sm font-medium focus:outline-none transition-colors duration-200"
        >
          <FaFilter className="mr-2" />
          <span>Advanced Filters</span>
          {isAdvancedFilterOpen ? (
            <FaChevronUp className="ml-2" />
          ) : (
            <FaChevronDown className="ml-2" />
          )}
        </button>
        
        {/* Advanced Filter Panel */}
        <AnimatePresence>
          {isAdvancedFilterOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200">
                <div className="bg-white px-4 py-3 rounded-lg shadow-sm border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 text-sm"
                  >
                    <option value="all">All Categories</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="bg-white px-4 py-3 rounded-lg shadow-sm border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transmission
                  </label>
                  <select
                    value={selectedTransmission}
                    onChange={(e) => setSelectedTransmission(e.target.value)}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 text-sm"
                  >
                    <option value="all">All Transmissions</option>
                    <option value="manual">Manual</option>
                    <option value="automatic">Automatic</option>
                  </select>
                </div>
                
                <div className="bg-white px-4 py-3 rounded-lg shadow-sm border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fuel Type
                  </label>
                  <select
                    value={selectedFuelType}
                    onChange={(e) => setSelectedFuelType(e.target.value)}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 text-sm"
                  >
                    <option value="all">All Fuel Types</option>
                    <option value="gasoline">Gasoline</option>
                    <option value="diesel">Diesel</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="electric">Electric</option>
                  </select>
                </div>
                
                <div className="bg-white px-4 py-3 rounded-lg shadow-sm border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min. Price (€ / day)
                  </label>
                  <input 
                    type="number" 
                    min="0"
                    placeholder="0"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 text-sm"
                  />
                </div>
                
                <div className="bg-white px-4 py-3 rounded-lg shadow-sm border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max. Price (€ / day)
                  </label>
                  <input 
                    type="number" 
                    min="0"
                    placeholder="500"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 text-sm"
                  />
                </div>
                
                <div className="bg-white px-4 py-3 rounded-lg shadow-sm border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min. Seats
                  </label>
                  <select
                    value={minSeats}
                    onChange={(e) => setMinSeats(e.target.value)}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 text-sm"
                  >
                    <option value="all">Any</option>
                    <option value="2">2+</option>
                    <option value="4">4+</option>
                    <option value="5">5+</option>
                    <option value="7">7+</option>
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end mt-4 space-x-3">
                <button
                  onClick={resetAllFilters}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                >
                  Reset Filters
                </button>
                <button
                  onClick={() => setIsAdvancedFilterOpen(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  Apply Filters
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Category Tabs Navigation with Vehicle Counts */}
      <div className="mb-6 overflow-x-auto">
        <div className="flex space-x-2 pb-2">
          <motion.button            
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setSelectedCategory("all");
              setCurrentFilteredPage(1);
            }}
            className={`px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              selectedCategory === "all"
                ? "bg-blue-600 text-white shadow-md"
                : "bg-white text-gray-700 hover:bg-gray-100"
            }`}
          >
            <span className="flex items-center">
              <FaCar className="mr-2" />
              All Vehicles ({vehicleStats.totalVehicles})
            </span>
          </motion.button>
          
          {categories.map((category) => (
            <motion.button
              key={category}              
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setSelectedCategory(category);
                setCurrentFilteredPage(1);
              }}
              className={`px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                selectedCategory === category
                  ? "bg-blue-600 text-white shadow-md"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
            >
              <span className="flex items-center">
                {getCategoryIcon(category)}
                {category} ({vehicleStats.categoryCounts[category] || 0})
              </span>
            </motion.button>
          ))}
        </div>
      </div>
      
      {/* Empty State */}
      {hasNoCars && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-xl shadow-md p-10 text-center"
        >
          <div className="inline-flex items-center justify-center p-4 bg-blue-100 rounded-full mb-4">
            <FaCar className="w-8 h-8 text-blue-600" />
          </div>          <h3 className="text-xl font-semibold text-gray-800 mb-2">No cars found</h3>
          <p className="text-gray-600 mb-6">
            {searchQuery ? 
              `No cars match your search "${searchQuery}". ${isLicensePlateSearch ? 'No vehicles found with that license plate.' : 'Try different keywords or clear your filters.'}` :
              `There are no cars matching your current filters. ${
                activeStatusFilter !== 'all' || selectedCategory !== 'all' || selectedTransmission !== 'all' || 
                selectedFuelType !== 'all' || minPrice !== '' || maxPrice !== '' || minSeats !== 'all' ?
                'Try changing or clearing your filter criteria.' : 'Please add some cars to your inventory.'
              }`}
          </p>
          <button
            onClick={() => {
              setSearchQuery("");
              setSelectedCategory("all");
              setSelectedTransmission("all");
              setActiveStatusFilter("all");
              setSelectedFuelType("all");
              setMinPrice("");
              setMaxPrice("");
              setMinSeats("all");
              setSortOption("newest");
              setIsLicensePlateSearch(false);
              setLicensePlateResults([]);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Clear All Filters
          </button>
        </motion.div>
      )}

      {/* Car Cards - Conditional Rendering Based on View Mode */}
      {!hasNoCars && viewMode === "grid" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCars.map((car) => (
            <motion.div
              key={car._id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ y: -8, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
              transition={{ duration: 0.25 }}
              className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:border-blue-300 transition-all duration-300"
            >
              {/* Enhanced Card with ribbon and better image display */}
              <div className="relative aspect-w-16 aspect-h-9 bg-gray-100">
                <img
                  src={getImagePath(car.images[0])}
                  alt={car.name}
                  className="object-contain w-full h-[200px] p-4"
                  onError={(e) => {
                    e.target.onerror = null;
                    if (window.location.hostname === "localhost") {
                      e.target.src = `https://rent-server.create-site.gr${getImagePath(car.images[0])}`;
                    }
                  }}
                />
                <div className="absolute top-0 left-0 right-0 flex justify-between items-start p-4">
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium shadow-sm ${
                      car.status === "available"
                        ? "bg-green-500 text-white"
                        : car.status === "maintenance"
                        ? "bg-yellow-500 text-white"
                        : "bg-red-500 text-white"
                    }`}
                  >
                    {car.status === "available" && <FaCheckCircle className="mr-1" />}
                    {car.status === "maintenance" && <FaTools className="mr-1" />}
                    {car.status === "unavailable" && <FaBan className="mr-1" />}
                    {car.status?.charAt(0).toUpperCase() + car.status?.slice(1) ||
                      "Available"}
                  </span>
                  <div>
                    {car.isNew && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-500 text-white shadow-sm">
                        New
                      </span>
                    )}
                    {isLicensePlateSearch && car.matchingVehicles && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-500 text-white shadow-sm ml-2">
                        License Match
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      {car.make} {car.model}
                    </h3>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                        {car.category}
                      </span>
                      <span className="mx-2 text-gray-300">•</span>
                      <span className="text-sm text-gray-500">{car.year}</span>
                    </div>
                    {isLicensePlateSearch && car.matchingVehicles && car.matchingVehicles.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs text-purple-600 font-medium">
                          License Plates: {car.matchingVehicles.map(v => v.licensePlate).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleEditCar(car)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                  >
                    <FaEdit className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-y-3 gap-x-4 mb-4">
                  <div className="flex items-center text-gray-600">
                    <FaUser className="w-4 h-4 mr-2 text-blue-500" />
                    <span>{car.seats} Seats</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <FaCog className="w-4 h-4 mr-2 text-blue-500" />
                    <span>{car.transmission}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <FaSuitcase className="w-4 h-4 mr-2 text-blue-500" />
                    <span>{car.suitcases} Suitcases</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <FaGasPump className="w-4 h-4 mr-2 text-blue-500" />
                    <span>{car.fuelType}</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-sm text-gray-500">Daily Rate</span>
                      <p className="text-lg font-bold text-blue-600">
                        €{car.basePrice}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-sm text-gray-500">
                        {isLicensePlateSearch && car.matchingVehicles ? 'Matching Vehicles' : 'Inventory'}
                      </span>
                      <p className="text-lg font-bold text-gray-900">
                        {isLicensePlateSearch && car.matchingVehicles ? 
                          car.matchingVehicles.length : 
                          (car.totalUnits || 0)
                        } <span className="text-sm font-normal">
                          {isLicensePlateSearch && car.matchingVehicles ? 'vehicles' : 'units'}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
      
      {/* List View */}
      {!hasNoCars && viewMode === "list" && (
        <div className="space-y-4">
          {filteredCars.map((car) => (
            <motion.div
              key={car._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ 
                scale: 1.01, 
                boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
              }}
              className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:border-blue-300 transition-all duration-300"
            >
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/4 p-4 flex items-center justify-center bg-gray-50">
                  <img
                    src={getImagePath(car.images[0])}
                    alt={car.name}
                    className="object-contain h-[140px] w-full"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/images/placeholder-car.png";
                    }}
                  />
                </div>
                
                <div className="md:w-3/4 p-4">
                  <div className="flex flex-wrap justify-between items-start mb-2">
                    <div className="mb-2 md:mb-0">
                      <div className="flex items-center">
                        <h3 className="text-lg font-bold text-gray-900">
                          {car.make} {car.model}
                        </h3>
                        <span
                          className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            car.status === "available"
                              ? "bg-green-100 text-green-800"
                              : car.status === "maintenance"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {car.status?.charAt(0).toUpperCase() + car.status?.slice(1) ||
                            "Available"}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {car.year} • {car.category} • {car.fuelType} • {car.transmission}
                      </p>
                    </div>
                    
                    <div className="flex space-x-2">
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Price</p>
                        <p className="text-lg font-bold text-blue-600">€{car.basePrice}/day</p>
                      </div>
                      <button
                        onClick={() => handleEditCar(car)}
                        className="ml-4 p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200 self-start"
                      >
                        <FaEdit className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="flex items-center text-gray-600 text-sm">
                      <FaUser className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{car.seats} Seats</span>
                    </div>
                    <div className="flex items-center text-gray-600 text-sm">
                      <FaSuitcase className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{car.suitcases} Suitcases</span>
                    </div>
                    <div className="flex items-center text-gray-600 text-sm">
                      <FaRegCalendarAlt className="w-4 h-4 mr-2 text-blue-500" />
                      <span>Units: {car.totalUnits || 0}</span>
                    </div>
                    <div className="flex items-center text-gray-600 text-sm">
                      <FaTags className="w-4 h-4 mr-2 text-blue-500" />
                      <span>ID: {car._id.substring(car._id.length - 6)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}      {/* Enhanced Pagination Controls with Jump to Page */}
      {filteredPages > 1 && (
        <div className="flex flex-row justify-center items-center mt-8 bg-white p-4 rounded-lg shadow-sm">

          
          <div className="flex  justify-center items-center space-x-2 mt-3 sm:mt-0 w-full">
            <button
              onClick={() => setCurrentFilteredPage(1)}
              disabled={currentFilteredPage === 1}
              className="px-3 py-2 rounded-md text-sm font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              First
            </button>
            <button
              onClick={() => setCurrentFilteredPage(Math.max(currentFilteredPage - 1, 1))}
              disabled={currentFilteredPage === 1}
              className="px-4 py-2 rounded-md text-sm font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            <div className="flex items-center">
              <span className="px-3 py-2 rounded-md text-sm font-medium bg-blue-50 text-blue-600 border border-blue-200">
                {currentFilteredPage}
              </span>
              <span className="mx-1 text-gray-500">of</span>
              <span className="px-3 py-2 rounded-md text-sm font-medium bg-gray-50 text-gray-600 border border-gray-200">
                {filteredPages}
              </span>
            </div>
            
            <button
              onClick={() => setCurrentFilteredPage(Math.min(currentFilteredPage + 1, filteredPages))}
              disabled={currentFilteredPage === filteredPages}
              className="px-4 py-2 rounded-md text-sm font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() => setCurrentFilteredPage(filteredPages)}
              disabled={currentFilteredPage === filteredPages}
              className="px-3 py-2 rounded-md text-sm font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Last
            </button>
          </div>
        </div>      )}

      {/* Create Car Modal */}
      <CreateCarModal
        isOpen={createCarModalOpen}
        onClose={() => setCreateCarModalOpen(false)}
        onSave={handleCreateCar}
        isCreating={isCreatingCar}
      />

      {/* Car Details Modal */}
      {selectedCar && (
        <CarDetailsModal
          car={selectedCar}
          isOpen={carDetailsModalOpen}
          onClose={() => {
            setCarDetailsModalOpen(false);
            setSelectedCar(null);
          }}
          onSave={(data, isImageUpdate) => handleCarUpdateWithImages(selectedCar._id, data, isImageUpdate)}
          onDelete={handleCarDelete}
          onSeasonSelect={handleSeasonSelect}
          isUpdating={updatingCarId === selectedCar._id}
          isDeleting={deletingCarId === selectedCar._id}
          settingSeasonalPrice={settingSeasonalPrice}
          categories={categories}
          editingSeasonalPrice={editingSeasonalPrice}
          onEditingSeasonalPriceChange={setEditingSeasonalPrice}
          seasonalPricing={seasonalPricing}
        />
      )}
        </>
      )}
      

      {/* Pricing Management Tab Content */}
      {activeTab === "pricing" && (        <PricingManagement 
          carsData={carsData}
          selectedCars={selectedCars}
          setSelectedCars={setSelectedCars}
          pricingMode={pricingMode}
          setPricingMode={setPricingMode}
          dateRange={dateRange}
          setDateRange={setDateRange}
          pricingData={pricingData}
          loadingPricing={loadingPricing}
          savingPricing={savingPricing}
          bulkPriceValue={bulkPriceValue}
          setBulkPriceValue={setBulkPriceValue}
          bulkPriceType={bulkPriceType}
          setBulkPriceType={setBulkPriceType}
          showPricingCalendar={showPricingCalendar}
          setShowPricingCalendar={setShowPricingCalendar}
          selectedPricingCar={selectedPricingCar}
          setSelectedPricingCar={setSelectedPricingCar}
          loadCarPricing={loadCarPricing}
          updateCarPricing={updateCarPricing}
          handleBulkPriceUpdate={handleBulkPriceUpdate}
          copyPricingFromCar={copyPricingFromCar}
          calculatePricingStats={calculatePricingStats}          seasonalPrices={seasonalPrices}
          setSeasonalPrices={setSeasonalPrices}
          getSeasonForDate={getSeasonForDate}
          LoadingSpinner={LoadingSpinner}
        />
      )}
    </>
  );
}

// Pricing Management Component
const PricingManagement = ({
  carsData,
  selectedCars,
  setSelectedCars,
  pricingMode,
  setPricingMode,
  dateRange,
  setDateRange,
  pricingData,
  loadingPricing,
  savingPricing,
  bulkPriceValue,
  setBulkPriceValue,
  bulkPriceType,
  setBulkPriceType,
  showPricingCalendar,
  setShowPricingCalendar,
  selectedPricingCar,
  setSelectedPricingCar,
  loadCarPricing,
  updateCarPricing,
  handleBulkPriceUpdate,
  copyPricingFromCar,
  calculatePricingStats,
  seasonalPrices,
  setSeasonalPrices,
  getSeasonForDate,
  LoadingSpinner
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortBy, setSortBy] = useState("make");

  // Filter and sort cars for pricing management
  const filteredCars = useMemo(() => {
    let result = [...carsData];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(car => 
        car.make?.toLowerCase().includes(query) ||
        car.model?.toLowerCase().includes(query) ||
        car.category?.toLowerCase().includes(query)
      );
    }

    if (selectedCategory !== "all") {
      result = result.filter(car => car.category === selectedCategory);
    }

    // Sort cars
    result.sort((a, b) => {
      switch (sortBy) {
        case "make":
          return a.make?.localeCompare(b.make) || 0;
        case "price":
          return (a.basePrice || 0) - (b.basePrice || 0);
        case "category":
          return a.category?.localeCompare(b.category) || 0;
        default:
          return 0;
      }
    });

    return result;
  }, [carsData, searchQuery, selectedCategory, sortBy]);

  const handleCarSelection = (carId) => {
    setSelectedCars(prev => 
      prev.includes(carId) 
        ? prev.filter(id => id !== carId)
        : [...prev, carId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCars.length === filteredCars.length) {
      setSelectedCars([]);
    } else {
      setSelectedCars(filteredCars.map(car => car._id));
    }
  };

  const generateDateRange = (start, end) => {
    const dates = [];
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d).toISOString().split('T')[0]);
    }
    
    return dates;
  };
  const getCurrentPricing = (car, date) => {
    if (!car.availability) return car.basePrice;
    
    const pricing = car.availability.find(
      a => new Date(a.date).toISOString().split('T')[0] === date
    );
    
    return pricing?.dynamicPrice || car.basePrice;
  };
  // Helper function for seasonal bulk updates


    const seasonalSettings = useMemo(() => {
    return {
      low: {
        label: "Low Season",
        price: seasonalPrices.low,
        multiplier: seasonalPrices.low / 100,
        description: "April 1 - May 15, September 21 - October 30"
      },
      medium: {
        label: "Medium Season",
        price: seasonalPrices.medium,
        multiplier: seasonalPrices.medium / 100,
        description: "May 16 - June 15, September 6 - September 20"
      },
      high: {
        label: "High Season",
        price: seasonalPrices.high,
        multiplier: seasonalPrices.high / 100,
        description: "June 16 - September 5"
      },
      custom: {
        label: "Custom Season",
        price: "Custom Price",
        multiplier: seasonalPrices.custom / 100,
        description: "Set your own custom price"
      }
    };
  }, [seasonalPrices]);

  const getSeasonMultiplier = (season) => {
    switch (season) {
      case "low":
        return seasonalPrices.low / 100;
      case "medium":
        return seasonalPrices.medium / 100;
      case "high":
        return seasonalPrices.high / 100;
      case "custom":
        return seasonalPrices.custom / 100;
      default:
        return 1; // Default to no multiplier
    }
  };

    const handleSeasonalBulkUpdate = async (season) => {
    if (selectedCars.length === 0) {
      return;
    }

    const multiplier = getSeasonMultiplier(season);
    const dates = generateDateRange(dateRange.startDate, dateRange.endDate);
    
    for (const carId of selectedCars) {
      const car = carsData.find(c => c._id === carId);
      if (!car) continue;

      const pricingUpdates = dates.map(date => {
        const seasonPrice = Math.round(car.basePrice * multiplier * 100) / 100;
        return {
          date: date,
          isAvailable: true,
          dynamicPrice: seasonPrice
        };
      });

      await updateCarPricing(carId, pricingUpdates);
    }

    // Clear selections
    setSelectedCars([]);
  };

  return (
    <div className="space-y-6">
      {/* Pricing Management Header */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <FaDollarSign className="mr-3 text-green-600" />
              Pricing Management
            </h2>
            <p className="text-gray-600 mt-1">
              Manage seasonal pricing, bulk updates, and pricing strategies for your vehicle fleet
            </p>
          </div>
        </div>

        {/* Pricing Mode Toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1 mb-6 w-fit">
          <button
            onClick={() => setPricingMode("single")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              pricingMode === "single"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-gray-600 hover:bg-gray-200"
            }`}
          >
            <FaEdit className="mr-2 inline" />
            Single Car Pricing
          </button>
          <button
            onClick={() => setPricingMode("bulk")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              pricingMode === "bulk"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-gray-600 hover:bg-gray-200"
            }`}
          >
            <FaCopy className="mr-2 inline" />
            Bulk Operations
          </button>
        </div>

        {/* Bulk Operations Panel */}
        {pricingMode === "bulk" && (
          <>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
              <FaCopy className="mr-2" />
              Bulk Pricing Operations
            </h3>
            
            {/* Seasonal Pricing Configuration */}
            <div className="bg-white rounded-lg p-4 mb-4">
              <h4 className="text-md font-medium text-gray-800 mb-3">Configure Seasonal Pricing (User-Specified Prices)</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-green-700">
                    Low Season Price (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={seasonalPrices.low}
                    onChange={(e) => setSeasonalPrices(prev => ({ ...prev, low: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter low season price"
                  />                  <div className="text-xs text-gray-600">
                    Apr 1 - Jun 18, Sep 16 - Oct 30
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-yellow-700">
                    Medium Season Price (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={seasonalPrices.medium}
                    onChange={(e) => setSeasonalPrices(prev => ({ ...prev, medium: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter medium season price"
                  />                  <div className="text-xs text-gray-600">
                    Jun 19 - Jul 10, Aug 26 - Sep 15
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-red-700">
                    High Season Price (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={seasonalPrices.high}
                    onChange={(e) => setSeasonalPrices(prev => ({ ...prev, high: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter high season price"
                  />
                  <div className="text-xs text-gray-600">
                    Jul 11 - Aug 25
                  </div>
                </div>
              </div>
            </div>

            {/* Seasonal Quick Actions */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Apply Seasonal Pricing to Selected Cars:</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <button
                  onClick={() => handleSeasonalBulkUpdate('low')}
                  disabled={selectedCars.length === 0 || !seasonalPrices.low || savingPricing}
                  className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {savingPricing ? (
                    <div className="flex items-center justify-center">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Applying...</span>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="font-medium">Apply Low Season</div>
                      <div className="text-sm">€{seasonalPrices.low || '0'}/day</div>
                    </div>
                  )}
                </button>
                
                <button
                  onClick={() => handleSeasonalBulkUpdate('medium')}
                  disabled={selectedCars.length === 0  || !seasonalPrices.medium || savingPricing}
                  className="px-4 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {savingPricing ? (
                    <div className="flex items-center justify-center">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Applying...</span>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="font-medium">Apply Medium Season</div>
                      <div className="text-sm">€{seasonalPrices.medium || '0'}/day</div>
                    </div>
                  )}
                </button>
                
                <button
                  onClick={() => handleSeasonalBulkUpdate('high')}
                  disabled={selectedCars.length === 0  || !seasonalPrices.high || savingPricing}
                  className="px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {savingPricing ? (
                    <div className="flex items-center justify-center">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Applying...</span>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="font-medium">Apply High Season</div>
                      <div className="text-sm">€{seasonalPrices.high || '0'}/day</div>
                    </div>
                  )}
                </button>
              </div>
            </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price Type
                </label>
                <select
                  value={bulkPriceType}
                  onChange={(e) => setBulkPriceType(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="fixed">Fixed Price (€)</option>
                  <option value="percentage">Percentage Change (%)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {bulkPriceType === "fixed" ? "Price (€)" : "Percentage (%)"}
                </label>
                <input
                  type="number"
                  value={bulkPriceValue}
                  onChange={(e) => setBulkPriceValue(e.target.value)}
                  placeholder={bulkPriceType === "fixed" ? "Enter price" : "Enter percentage"}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={handleBulkPriceUpdate}
                  disabled={!dateRange.startDate || !dateRange.endDate || selectedCars.length === 0 || !bulkPriceValue || savingPricing}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {savingPricing ? (
                    <div className="flex items-center justify-center">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Updating...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <FaSave className="mr-2" />
                      Apply to {selectedCars.length} Cars
                    </div>
                  )}
                </button>
              </div>
            </div>
            </>
        )}
      </div>


      {/* Cars Grid with Pricing */}
      <div className="bg-white rounded-xl shadow-md p-6">
        {/* Search and Filter Controls */}
        <div className="flex flex-wrap gap-4 items-center justify-between mb-6">
          <div className="flex-1 relative min-w-[250px]">
            <input
              type="text"
              placeholder="Search cars by make, model, or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
          </div>
          
          <div className="flex gap-4">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              <option value="Economy">Economy</option>
              <option value="Standard">Standard</option>
              <option value="Jeep-SUV-Family">Jeep-SUV-Family</option>
              <option value="Cabrio">Cabrio</option>
              <option value="Mini Vans">Mini Vans</option>
              <option value="Motorbikes">Motorbikes</option>
              <option value="ATV-Quads">ATV-Quads</option>
              <option value="Electric">Electric</option>
              <option value="Special">Special</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="make">Sort by Make</option>
              <option value="price">Sort by Price</option>
              <option value="category">Sort by Category</option>
            </select>
          </div>
        </div>

        {/* Select All Checkbox */}
        {pricingMode === "bulk" && (
          <div className="flex items-center mb-4 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="selectAll"
              checked={selectedCars.length === filteredCars.length && filteredCars.length > 0}
              onChange={handleSelectAll}
              className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="selectAll" className="text-sm font-medium text-gray-700">
              Select All Cars ({filteredCars.length} cars)
            </label>
          </div>
        )}

        {/* Cars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCars.map((car) => {
            const stats = calculatePricingStats(car);
            const isSelected = selectedCars.includes(car._id);
            
            return (
              <motion.div
                key={car._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`border rounded-xl overflow-hidden transition-all duration-200 ${
                  isSelected && pricingMode === "bulk"
                    ? "border-blue-500 bg-blue-50 shadow-lg"
                    : "border-gray-200 hover:border-gray-300 hover:shadow-md"
                }`}
              >
                {/* Selection Checkbox for Bulk Mode */}
                {pricingMode === "bulk" && (
                  <div className="p-3 border-b border-gray-200">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleCarSelection(car._id)}
                        className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Select for bulk operations
                      </span>
                    </label>
                  </div>
                )}

                {/* Car Info */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {car.make} {car.model}
                      </h3>
                      <p className="text-sm text-gray-600">{car.category}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">
                        €{car.basePrice}/day
                      </p>
                      <p className="text-xs text-gray-500">Base Price</p>
                    </div>
                  </div>                  {/* Pricing Stats with Seasonal Breakdown */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                      <FaChartLine className="mr-1" />
                      Pricing Statistics
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                      <div>
                        <span className="text-gray-500">Avg Price:</span>
                        <span className="ml-1 font-medium">€{stats.averagePrice}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Min Price:</span>
                        <span className="ml-1 font-medium">€{stats.minPrice}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Max Price:</span>
                        <span className="ml-1 font-medium">€{stats.maxPrice}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Priced Days:</span>
                        <span className="ml-1 font-medium">{stats.totalDays}</span>
                      </div>
                    </div>

                    {/* Seasonal Breakdown */}
                    <div className="space-y-1">
                      <h5 className="text-xs font-medium text-gray-600 mb-2">Seasonal Breakdown:</h5>
                      {Object.entries(stats.seasonalBreakdown).map(([season, data]) => (
                        data.count > 0 && (
                          <div key={season} className="flex justify-between items-center text-xs">
                            <span className={`px-2 py-1 rounded capitalize ${
                              season === 'low' ? 'bg-green-100 text-green-800' :
                              season === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              season === 'high' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {season}
                            </span>
                            <span className="text-gray-600">
                              {data.count} days @ €{data.avgPrice}
                            </span>
                          </div>
                        )
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedPricingCar(car)}
                      className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <FaCalendarAlt className="mr-1 inline" />
                      Edit Pricing
                    </button>
                    
                    {pricingMode === "bulk" && selectedCars.length > 0 && !selectedCars.includes(car._id) && (
                      <button
                        onClick={() => copyPricingFromCar(car, selectedCars)}
                        disabled={savingPricing}
                        className="px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                        title="Copy this car's pricing to selected cars"
                      >
                        <FaCopy />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {filteredCars.length === 0 && (
          <div className="text-center py-12">
            <FaCar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No cars found</h3>
            <p className="text-gray-600">
              Try adjusting your search criteria or filters.
            </p>
          </div>
        )}
      </div>      {/* Pricing Calendar Modal */}
      {selectedPricingCar && (
        <PricingCalendarModal
          car={selectedPricingCar}
          isOpen={!!selectedPricingCar}
          onClose={() => setSelectedPricingCar(null)}
          dateRange={dateRange}
          onSave={updateCarPricing}
          getCurrentPricing={getCurrentPricing}
          generateDateRange={generateDateRange}
          seasonalPrices={seasonalPrices}
          getSeasonForDate={getSeasonForDate}
          savingPricing={savingPricing}
          LoadingSpinner={LoadingSpinner}
        />
      )}
    </div>
  );
};

// Pricing Calendar Modal Component
const PricingCalendarModal = ({
  car,
  isOpen,
  onClose,
  dateRange,
  onSave,
  getCurrentPricing,
  generateDateRange,
  seasonalPrices,
  getSeasonForDate,
  savingPricing,
  LoadingSpinner
}) => {
  const [pricingChanges, setPricingChanges] = useState({});
  const [viewMode, setViewMode] = useState("calendar"); // "calendar" or "list"
  const [customPercentage, setCustomPercentage] = useState("");
  const [customFixedPrice, setCustomFixedPrice] = useState("");
  const [selectedSeason, setSelectedSeason] = useState("");

  const getSeasonColor = (season) => {
    switch (season) {
      case 'low': return 'bg-green-100 border-green-300 text-green-800';
      case 'medium': return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      case 'high': return 'bg-red-100 border-red-300 text-red-800';
      default: return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  };

  useEffect(() => {
    if (isOpen && dateRange.startDate && dateRange.endDate) {
      // Initialize pricing changes with current values from availability array
      const dates = generateDateRange(dateRange.startDate, dateRange.endDate);
      const initialPricing = {};
      
      dates.forEach(date => {
        initialPricing[date] = getCurrentPricing(car, date);
      });
      
      setPricingChanges(initialPricing);
    }
  }, [isOpen, dateRange, car]);

  const handlePriceChange = (date, price) => {
    setPricingChanges(prev => ({
      ...prev,
      [date]: parseFloat(price) || 0
    }));
  };

  const handleSave = async () => {
    const updates = Object.entries(pricingChanges).map(([date, price]) => ({
      date,
      isAvailable: true,
      dynamicPrice: price
    }));

    const success = await onSave(car._id, updates);
    if (success) {
      onClose();
    }
  };

  const applyToAllDates = () => {
    const basePrice = car.basePrice;
    const updatedPricing = {};
    
    Object.keys(pricingChanges).forEach(date => {
      updatedPricing[date] = basePrice;
    });
    
    setPricingChanges(updatedPricing);
  };
  const applySeasonalPricing = (season) => {
    if (!seasonalPrices[season]) return;
    
    const updatedPricing = {};
    const seasonPrice = parseFloat(seasonalPrices[season]);
    
    Object.keys(pricingChanges).forEach(date => {
      updatedPricing[date] = seasonPrice;
    });
    
    setPricingChanges(updatedPricing);
  };

  const applyCustomPercentage = () => {
    if (!customPercentage) return;
    
    const percentage = parseFloat(customPercentage);
    const updatedPricing = {};
    
    Object.entries(pricingChanges).forEach(([date, currentPrice]) => {
      const newPrice = currentPrice + (currentPrice * percentage / 100);
      updatedPricing[date] = Math.round(newPrice * 100) / 100;
    });
    
    setPricingChanges(updatedPricing);
    setCustomPercentage("");
  };

  const applyCustomFixedPrice = () => {
    if (!customFixedPrice) return;
    
    const price = parseFloat(customFixedPrice);
    const updatedPricing = {};
    
    Object.keys(pricingChanges).forEach(date => {
      updatedPricing[date] = price;
    });
    
    setPricingChanges(updatedPricing);
    setCustomFixedPrice("");
  };  const applySeasonalPricingToSelectedDates = () => {
    if (!selectedSeason || !seasonalPrices[selectedSeason]) return;
    
    const seasonPrice = parseFloat(seasonalPrices[selectedSeason]);
    const updatedPricing = {};
    
    Object.entries(pricingChanges).forEach(([date, currentPrice]) => {
      const dateObj = new Date(date);
      const dateSeasonFromDate = getSeasonForDate(dateObj);
      
      // Only apply to dates that match the selected season or apply to all if "all" is selected
      if (selectedSeason === 'all' || dateSeasonFromDate === selectedSeason) {
        updatedPricing[date] = seasonPrice;
      } else {
        updatedPricing[date] = currentPrice; // Keep existing price
      }
    });
    
    setPricingChanges(updatedPricing);
  };

  // Apply percentage change to all dates
  const applyPercentageChange = (percentage) => {
    const updatedPricing = {};
    
    Object.entries(pricingChanges).forEach(([date, currentPrice]) => {
      const newPrice = currentPrice + (currentPrice * percentage / 100);
      updatedPricing[date] = Math.round(newPrice * 100) / 100;
    });
    
    setPricingChanges(updatedPricing);
  };

  if (!isOpen) return null;

  const dates = dateRange.startDate && dateRange.endDate 
    ? generateDateRange(dateRange.startDate, dateRange.endDate)
    : [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">
                Pricing Calendar - {car.make} {car.model}
              </h2>
              <p className="text-blue-100 mt-1">
                Base Price: €{car.basePrice}/day | Category: {car.category}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-blue-700 p-2 rounded-lg transition-colors"
            >
              <FaArrowLeft className="w-5 h-5" />
            </button>
          </div>
        </div>        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">          {/* Seasonal Pricing Controls */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-6 border border-blue-200">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
              <FaChartLine className="mr-2 text-blue-600" />
              Seasonal Pricing Controls (User-Specified Prices)
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <button
                onClick={() => applySeasonalPricing('low')}
                disabled={!seasonalPrices.low}
                className="px-4 py-3 bg-green-100 border-2 border-green-300 text-green-800 rounded-lg hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-center"
              >
                <div className="font-semibold">Low Season</div>
                <div className="text-sm">€{seasonalPrices.low || '0'}/day</div>
                <div className="text-xs opacity-75">Apr-Jun 18, Sep 16-Oct 30</div>
              </button>
              
              <button
                onClick={() => applySeasonalPricing('medium')}
                disabled={!seasonalPrices.medium}
                className="px-4 py-3 bg-yellow-100 border-2 border-yellow-300 text-yellow-800 rounded-lg hover:bg-yellow-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-center"
              >
                <div className="font-semibold">Medium Season</div>
                <div className="text-sm">€{seasonalPrices.medium || '0'}/day</div>
                <div className="text-xs opacity-75">Jun 19-Jul 10, Aug 26-Sep 15</div>
              </button>
              
              <button
                onClick={() => applySeasonalPricing('high')}
                disabled={!seasonalPrices.high}
                className="px-4 py-3 bg-red-100 border-2 border-red-300 text-red-800 rounded-lg hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-center"
              >
                <div className="font-semibold">High Season</div>
                <div className="text-sm">€{seasonalPrices.high || '0'}/day</div>
                <div className="text-xs opacity-75">Jul 11-Aug 25</div>
              </button>
              
              <button
                onClick={applyToAllDates}
                className="px-4 py-3 bg-gray-100 border-2 border-gray-300 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors text-center"
              >
                <div className="font-semibold">Base Price</div>
                <div className="text-sm">€{car.basePrice}</div>
                <div className="text-xs opacity-75">Reset all dates</div>
              </button>
            </div>
            
            <div className="text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded p-2">
              <strong>Note:</strong> Configure seasonal prices in the main Pricing Management tab before using these quick actions.
            </div>
          </div>

          {/* Custom Pricing Controls */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
              <FaCog className="mr-2 text-gray-600" />
              Custom Pricing Actions
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Percentage Adjustment */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Percentage Adjustment
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Enter %"
                    value={customPercentage}
                    onChange={(e) => setCustomPercentage(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={applyCustomPercentage}
                    disabled={!customPercentage}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm"
                  >
                    <FaPercent className="inline mr-1" />
                    Apply
                  </button>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {[5, 10, 15, 20, -5, -10, -15, -20].map(percent => (
                    <button
                      key={percent}
                      onClick={() => applyPercentageChange(percent)}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        percent > 0 
                          ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      }`}
                    >
                      {percent > 0 ? '+' : ''}{percent}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Fixed Price */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Fixed Price (€)
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Enter price"
                    value={customFixedPrice}
                    onChange={(e) => setCustomFixedPrice(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={applyCustomFixedPrice}
                    disabled={!customFixedPrice}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors text-sm"
                  >
                    <FaDollarSign className="inline mr-1" />
                    Apply
                  </button>
                </div>
                <div className="text-xs text-gray-600">
                  Will apply the same price to all selected dates
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Summary */}
          <div className="bg-white border rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-800 mb-3">Pricing Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center">
                <div className="font-medium text-blue-600">
                  €{dates.length > 0 ? Math.round((Object.values(pricingChanges).reduce((a, b) => a + b, 0) / dates.length) * 100) / 100 : car.basePrice}
                </div>
                <div className="text-gray-500">Avg Price</div>
              </div>
              <div className="text-center">
                <div className="font-medium text-green-600">
                  €{dates.length > 0 ? Math.min(...Object.values(pricingChanges)) : car.basePrice}
                </div>
                <div className="text-gray-500">Min Price</div>
              </div>
              <div className="text-center">
                <div className="font-medium text-red-600">
                  €{dates.length > 0 ? Math.max(...Object.values(pricingChanges)) : car.basePrice}
                </div>
                <div className="text-gray-500">Max Price</div>
              </div>
              <div className="text-center">
                <div className="font-medium text-purple-600">
                  €{dates.length > 0 ? Math.round(Object.values(pricingChanges).reduce((a, b) => a + b, 0) * 100) / 100 : 0}
                </div>
                <div className="text-gray-500">Total Revenue</div>
              </div>
            </div>
          </div>          {/* Seasonal Legend */}
          <div className="bg-white border rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-800 mb-3">Season Legend</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-green-100 border-2 border-green-300 rounded mr-2"></div>
                <span className="text-green-800 font-medium">Low Season</span>
                <span className="text-gray-500 ml-2 text-xs">Apr-Jun 18, Sep 16-Oct 30</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-yellow-100 border-2 border-yellow-300 rounded mr-2"></div>
                <span className="text-yellow-800 font-medium">Medium Season</span>
                <span className="text-gray-500 ml-2 text-xs">Jun 19-Jul 10, Aug 26-Sep 15</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-red-100 border-2 border-red-300 rounded mr-2"></div>
                <span className="text-red-800 font-medium">High Season</span>
                <span className="text-gray-500 ml-2 text-xs">Jul 11-Aug 25</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gray-100 border-2 border-gray-300 rounded mr-2"></div>
                <span className="text-gray-800 font-medium">Custom</span>
                <span className="text-gray-500 ml-2 text-xs">Other dates</span>
              </div>
            </div>
          </div>

          {/* Pricing Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {dates.map((date) => {
              const dateObj = new Date(date);
              const dayOfWeek = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
              const formattedDate = dateObj.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric' 
              });
              const currentPrice = pricingChanges[date] || car.basePrice;
              const isWeekend = [0, 6].includes(dateObj.getDay());
              const season = getSeasonForDate(dateObj);
              const seasonColor = getSeasonColor(season);
              const priceChange = currentPrice - car.basePrice;
              const priceChangePercentage = (priceChange / car.basePrice) * 100;
              
              return (
                <div
                  key={date}
                  className={`border-2 rounded-lg p-3 transition-all hover:shadow-md ${seasonColor} ${
                    isWeekend ? 'ring-2 ring-blue-200' : ''
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="font-semibold text-gray-900">{dayOfWeek}</div>
                      <div className="text-sm text-gray-600">{formattedDate}</div>
                    </div>
                    <div className="text-right">
                      {isWeekend && (
                        <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded mb-1">
                          Weekend
                        </div>
                      )}
                      <div className={`text-xs px-2 py-1 rounded font-medium ${
                        season === 'low' ? 'bg-green-200 text-green-800' :
                        season === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                        season === 'high' ? 'bg-red-200 text-red-800' :
                        'bg-gray-200 text-gray-800'
                      }`}>
                        {season.charAt(0).toUpperCase() + season.slice(1)} Season
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-gray-700">
                      Price (€)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={currentPrice}
                      onChange={(e) => handlePriceChange(date, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    
                    {priceChange !== 0 && (
                      <div className="text-xs space-y-1">
                        <div className={`font-medium ${
                          priceChange > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {priceChange > 0 ? '+' : ''}€{priceChange.toFixed(2)}
                        </div>
                        <div className={`${
                          priceChange > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          ({priceChangePercentage > 0 ? '+' : ''}{priceChangePercentage.toFixed(1)}%)
                        </div>
                      </div>
                    )}
                    
                    {priceChange === 0 && (
                      <div className="text-xs text-gray-500">
                        Base price
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>        {/* Modal Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {dates.length} days selected
            {dates.length > 0 && (
              <span className="ml-2 text-blue-600">
                • Total Revenue: €{Math.round(Object.values(pricingChanges).reduce((a, b) => a + b, 0) * 100) / 100}
              </span>
            )}
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={savingPricing}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {savingPricing ? (
                <div className="flex items-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Saving...</span>
                </div>
              ) : (
                <div className="flex items-center">
                  <FaSave className="mr-2" />
                  Save Pricing Changes
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cars;